import React, { useState, createContext, useEffect, useContext, useRef} from "react"
import { indexDeReprise, apresLaPartie, fermetureApresRelecture } from '../parcours/progression'
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import * as jsondiffpatch from 'jsondiffpatch';
import { matchPath } from 'react-router-dom';

import LanguageContext, { values as DICTIONNAIRE } from "./LanguageContext"
import { formeDepuisCible, choisirTexte } from "../utils/formeAdresse"
import PopupNotifContext from "./PopupNotifContext"


import APIS, { GLB_URL_THESCAN } from "../utils/APIS"
import { isLoggedIn, getCurrentUser, logout, setUser as setUserAuth } from "../utils/auth"
import { moyenneDesCategories, scoreDuModule } from "../utils/scoreGlobal"

const UserContext = createContext()

export const getPointsByQuizz = (quizz) => {
  var maxPoints = 0
  for (var res of quizz.responses) {
    var pts = parseInt(res.points)
    if (quizz.multi && pts > 0) maxPoints += pts
    if (!quizz.multi && pts > maxPoints) maxPoints = pts
  }

  if (quizz.userChoice === null || quizz.userChoice === undefined) {
    return [0, maxPoints]
  }
  var userPoints = 0
  if (typeof(quizz.userChoice) === "number") {
    let res = quizz.responses[quizz.userChoice]
    if (res && res.points) userPoints += parseInt(res.points)
  } else {
    for (var choice of quizz.userChoice) {
      let res = quizz.responses[choice]
      if (res && res.points) userPoints += parseInt(res.points)
    }
  }

  return [userPoints, maxPoints]
}

/* Au-dela, la relecture d'avant une fin de module est abandonnee (B73). */
const DELAI_RELECTURE_MS = 15000

const UserProvider = ({ children }) => {
  const navigate = useNavigate();

  const {
    actLanguage,
    changeLanguage,
    languageValues,
  } = useContext(LanguageContext)

  const { addPopupNotif } = useContext(PopupNotifContext)

  const [loaded, setLoaded] = useState(false)
  const [firstLoad, setFirstLoad] = useState(false)

  const [user, setUser] = useState(null)
  const [plan, setPlan] = useState(null)
  const [formation, setFormation] = useState(null)
  const [actIndex, setActIndex] = useState(null)
  const [lastDoneIndex, setLastDoneIndex] = useState(null)
  /* Relecture du module en cours avant une fin par parties vides (B73) :
     tant qu'elle n'est pas revenue, aucun clic ne fait avancer ni n'ecrit. */
  const relectureEnCours = useRef(false)
  const relectureNumero = useRef(0)
  /* Les enregistrements en vol : la relecture les attend (B73). */
  const enregistrementsEnVol = useRef(Promise.resolve())

  /* ⛔ ESSAI JETABLE D'UN MODULE DEPUIS L'ADMIN (21/09/2026, decision d'Hicham).
     Nawal doit pouvoir tester son module sans se deconnecter et sans compte
     apprenant : les reponses se valident, les feedbacks s'affichent, la
     progression avance, l'ecran de fin calcule un vrai resultat sur ses vraies
     reponses. UN SEUL point est coupe : l'enregistrement serveur.

     Pourquoi c'est si peu de code : la navigation du lecteur se fait DEJA
     entierement en memoire (`setFormation` + `setActIndex` dans `onIndexChange`).
     Seul `saveUserFormation` sort vers le serveur. Amorcer le contexte avec le
     module et couper cette fonction suffit donc.
     27/09/2026 : une deuxieme sortie existe, `repondreSequence` (reponse d'un
     candidat THE SCAN corrigee par le serveur). Elle est coupee de la meme
     facon, par la meme reference.

     ⚠️ `essaiRef` EN PLUS de l'etat : `saveUserFormation` est asynchrone et
     appelee depuis des fermetures ; un booleen d'etat peut y etre perime, une
     reference ne l'est jamais. C'est la garantie qu'aucune ecriture ne passe.

     ⚠️ On MEMORISE ce qu'il y avait avant. Un admin peut lui-meme etre
     inscrit a une formation : sans cette sauvegarde, lancer un essai ecraserait
     sa propre progression en memoire. `arreterEssai` la remet en place. */
  const [essai, setEssai] = useState(false)
  const essaiRef = useRef(false)
  const avantEssai = useRef(null)

  const demarrerEssai = (moduleAEssayer, planPourEssai) => {
    if (!moduleAEssayer) return
    avantEssai.current = { formation, actIndex, plan }
    const copie = JSON.parse(JSON.stringify(moduleAEssayer))
    /* Jetable : on repart d'une ardoise vierge a CHAQUE lancement. */
    copie.isDone = false
    /* 06/10/2026 : sans resultat non plus. L'ecran de fin ne calcule le score
       (en memoire) que si `resultFin` est NUL ; un module charge sans la cle
       n'en donnait aucun, donc aucun certificat (score global > 50 %). */
    copie.resultFin = null
    ;(copie.chapitre || []).forEach((ch) => {
      ch.isDone = false
      ;(ch.partie || []).forEach((pa) => {
        pa.isDone = false
        ;(pa.sequence || []).forEach((se) => { se.isDone = false; se.userChoice = null })
      })
    })
    essaiRef.current = true
    setEssai(true)
    setFormation(copie)
    if (planPourEssai) setPlan(planPourEssai)
    /* Le premier ecran REEL : une partie vide en tete donnait une page blanche
       dans l'essai de Nawal, qui est justement celle qui cree ces parties
       (B73, relecture du 02/10/2026). */
    setActIndex(indexDeReprise(copie))
  }

  const arreterEssai = () => {
    essaiRef.current = false
    setEssai(false)
    const avant = avantEssai.current
    if (avant) {
      setFormation(avant.formation)
      setActIndex(avant.actIndex)
      setPlan(avant.plan)
    }
    avantEssai.current = null
  }

  useEffect(() => {
    if (actLanguage && user?.language) {
      if (actLanguage !== user?.langauge) {
        changeLanguage(user?.language)
      }
    }
  }, [actLanguage, user])


  useEffect(() => {
    if (loaded === false) {
      updateUserContext()
    }
  }, [loaded])

  /* `encoreAttendue` (B73) : une relecture abandonnee, revenue apres son
     delai, ne pose plus rien (voir onIndexChange). */
  const updateUserContext = (id, forceRefreshStorage, encoreAttendue) => {
    /* Tout autre rechargement (quitter le module, resynchronisation...)
       abandonne la verification d'avant une fin de module : l'apprenant est
       ailleurs, et la copie de reference va changer sous elle (relecture de
       code de 97c82dd : le renvoi partait alors avec des champs internes, et
       le module se fermait pendant que l'apprenant etait au tableau de bord). */
    if (!encoreAttendue) relectureNumero.current += 1
    if (getCurrentUser()?.type === "theScanCandidat") {
      const matchPathname = matchPath({
        path: "/thescan/:id",
        exact: true,
        strict: false
      }, window?.location?.pathname);
      if (matchPathname && matchPathname?.params?.id) {
        id = matchPathname?.params?.id
      }
    }
    // 27/09/2026 : la promesse est rendue, pour que la resynchronisation
    // (saveUserFormation, 409 dejaRepondu) sache quand elle est finie.
    return APIS.getAllInfos(id).then((result) => {
      if (encoreAttendue && !encoreAttendue()) return
      let actUser = getCurrentUser()
      if (actUser.type === "theScanCandidat" && result?.error) {
        window.location.href = GLB_URL_THESCAN;
      }
      if ((actUser.type === "student" && result?.user?.type === 'student') || (actUser.type === "theScanCandidat" && result?.user?.type === 'theScanCandidat')) {
        /* B55 (25/09/2026) : ne pas afficher `result` dans la console du
           navigateur. Simple hygiene, PAS une protection : la meme reponse est
           dans l'onglet reseau, et sa formation, resultats compris, dans
           sessionStorage (`saveFormation`, plus bas). Qu'un candidat THE SCAN
           ne lise pas ses scores (PD-10) ne repose donc pas sur cette ligne. */

        //pour blocage des élèves dans des groupes archivés
        try {
          if (result?.user?.groupes?.length > 0) {
            let grps = result?.user?.groupes
            let findNonArchived = false
            for (let i=0; i<grps.length; i++) {
              if (grps[i]?.id === 1 || grps[i]?.archived === "false") { //soit groupe admin soit non archivé
                findNonArchived = true
                break;
              }
            }
            if (findNonArchived === false) {
              navigate("/noAccess")
              return
            }
          }
        } catch(e) { console.log(e) }

        if (result?.user && result?.plan && result?.formation) {
          setUser(result?.user)
          if (forceRefreshStorage) {
            APIS.getLoginStatus().then(result => {
              if (result?.message == "status" && result?.user) {
                setUserAuth(result?.user)
              }
            })
          }

          setPlan(result?.plan)

          sessionStorage.setItem('saveFormation', JSON.stringify(result?.formation));
          let calcActIndex = indexDeReprise(result?.formation)
          setFormation(result?.formation)
          setActIndex(calcActIndex)

          setLoaded(true)
          return result?.formation
        } else {
          addPopupNotif({
            uuid : uuidv4(),
            title : languageValues?.notifError[actLanguage],
            type : "error",
            removeAfter:5000,
          })
          setTimeout(() => {
            logout(() => { document?.location?.reload() })
          }, 3000)
        }
      }
    })
  }

  useEffect(() => {
    if (formation) {
      let calcActIndex = indexDeReprise(formation)
      setLastDoneIndex(calcActIndex)
    }
  }, [formation, actIndex])

  const calculateProgress = (formation) => {
    var data = { total: 0, done: 0 }
    for (var chapitre of formation.chapitre) {
      for (var partie of chapitre.partie) {
        for (var sequence of partie.sequence) {
          data.total++
          if (sequence.isDone) data.done++
        }
      }
    }

    return data.total > 0 ? Math.round((data.done / data.total) * 100) : 0
  }

  const sequencesDataRefacto = (seq) => {
    if (seq.userChoice && seq.userChoice !== null) {
      try {
        seq.userChoice = JSON.stringify(seq.userChoice)
      } catch(e) {
        seq.userChoice = seq.userChoice
      }
    }

    return seq
  }

  /* ⛔ L'APERCU ADMIN NE REPOND JAMAIS (21/09/2026). Capture d'Hicham : clic
     sur « Demarrer » en plein ecran, boite « Erreur ! ». Cause : cette fonction
     compare l'ecran clique avec l'ecran courant de la formation de L'UTILISATEUR
     CONNECTE. En apercu admin il n'y a pas de formation en cours, la comparaison
     echoue et on tombe dans le `else`.
     ⚠️ La boite n'etait pas le bug, c'etait le garde-fou : si les identifiants
     avaient correspondu, la branche du dessus appelait `saveUserFormation`, donc
     ECRIVAIT une progression depuis un apercu. On sort donc AVANT les deux
     branches, ce qui garantit qu'aucune reponse n'est soumise et qu'aucune
     progression n'est enregistree depuis la vue admin.
     Le parcours apprenant est intact : `vueApprenant=1` n'existe que sur la
     route admin, jamais sur celle d'un apprenant.
     ⛔ B55 (25/09/2026) : la phrase ci-dessus etait une intention, pas une
     garde. N'IMPORTE QUI pouvait ajouter `?vueApprenant=1` a son adresse : un
     candidat sur /thescan/:id?vueApprenant=1 passait alors le test « a blanc »,
     sans qu'aucune reponse ne soit enregistree. L'apercu exige desormais les
     TROIS conditions : une page admin (/admin/...), le parametre, et un
     utilisateur administrateur (`isAdmin`, celui que verifie deja la garde des
     routes admin dans App.js).
     ⛔ B58 (25/09/2026) : le parametre ne suffisait pas non plus DANS l'admin.
     « Plein ecran » et « Fin de module (simulee) » ouvrent la vue apprenant
     sans toucher a l'adresse : `vueApprenant=1` n'y figure que dans l'iframe
     d'apercu. Hors essai, un clic dans cette vue passait donc par
     l'enregistrement normal, comparait avec la formation DE L'ADMIN et
     affichait « Erreur ! » ; et si l'admin etait inscrit au meme module, au
     meme ecran, SA progression etait enregistree. La page du lecteur admin
     (FormationPlayer) le signale donc elle-meme, tant qu'elle est affichee,
     par `marquerApercuAdmin`. Le signal remplace le parametre, pas les deux
     autres conditions : page admin ET administrateur restent exiges.
     ⚠️ Une reference, pas un etat : rien ne s'affiche d'apres ce signal, il
     n'est lu qu'au moment d'un clic, et une reference n'est jamais perimee
     dans une fermeture (meme raison que `essaiRef`). */
  const apercuAdminRef = useRef(false)
  const marquerApercuAdmin = (actif) => { apercuAdminRef.current = actif === true }
  const estApercuAdmin = () => {
    try {
      const surPageAdmin = (window.location.pathname || "").startsWith("/admin/")
      const demande = apercuAdminRef.current === true ||
        new URLSearchParams(window.location.search).get("vueApprenant") === "1"
      return surPageAdmin && demande && user?.isAdmin === true
    }
    catch (e) { return false }
  }

  /* 27/09/2026 : `options` est transmis tel quel a saveUserFormation (voir
     `reponseDejaEnregistree` la-bas). */
  const manageFormatResponse = (actFormatVerif, datas, options) => {
    /* Hors essai, l'apercu admin reste INERTE : le contexte ne porte pas la
       formation regardee, la comparaison d'identifiants echouerait et l'ancien
       code alertait « Erreur ! ». Pendant un essai en revanche, le contexte
       PORTE bien le module : le chemin normal s'applique, et c'est
       `saveUserFormation` qui bloque l'ecriture. */
    if (estApercuAdmin() && !essaiRef.current) return
    let actFormat = formation?.chapitre[actIndex?.chapitre].partie[actIndex?.partie].sequence[actIndex?.sequence]
    if (actFormat?.id === actFormatVerif?.id) {
      if (!actFormat?.isDone) {
        for (const key of datas) {
          actFormat[key?.key] = key?.value
        }
        setFormation(formation)
        saveUserFormation(formation, options)
      }
    } else {
      alert("Erreur\u00a0!")
    }
  }

  const forceNewIndexes = (newIndexes) => {
    if (newIndexes?.chapitre >= 0 && newIndexes?.partie >= 0 && newIndexes?.sequence >= 0) {
      setActIndex({ chapitre: newIndexes?.chapitre, partie: newIndexes?.partie, sequence: newIndexes?.sequence })
    }
  }

  const manageSlide = (addIndex) => {
    /* ⛔ B58 (25/09/2026) : chaque format appelle `manageSlide(1)` juste apres
       sa reponse. En apercu admin, cet index est celui du PROPRE parcours de
       l'admin, pas de l'ecran regarde : le deplacer ne montre rien, et sur le
       dernier ecran d'une partie `onIndexChange("next")` ENREGISTRE la partie
       comme faite. L'apercu reste donc inerte ici aussi. Pendant un essai, le
       contexte porte le module essaye : on avance normalement. */
    if (estApercuAdmin() && !essaiRef.current) return null
    if (relectureEnCours.current) return null   // B73 : voir onIndexChange
    var newIndex = actIndex?.sequence + addIndex
    if (newIndex < 0) { return null }

    let actPart = formation?.chapitre[actIndex?.chapitre].partie[actIndex?.partie]
    if (newIndex >= actPart?.sequence?.length) {
      //on passe à la partie ou au chapitre suivant
      return onIndexChange("next")
    }

    //on passe au format suivant
    return onIndexChange(newIndex)
  }

  const onIndexChange = (newIndex) => {
    if (newIndex !== 'next') {
      return setActIndex({ chapitre: actIndex?.chapitre, partie: actIndex?.partie, sequence: newIndex })
    }
    /* La suite d'une partie finie se calcule dans `parcours/progression.js`,
       ou elle est testee (B73). */
    const { formation: actFormation, index, partiesVidesSautees } = apresLaPartie(formation, actIndex)
    /* ⛔ UNE FIN DE MODULE QUI NE TIENT QU'A DES PARTIES VIDES SE VERIFIE D'ABORD
       (relecture securite du 02/10/2026). Une partie vide est souvent la trace
       d'une edition EN COURS : si Nawal y ajoute des ecrans pendant qu'un
       candidat de THE SCAN passe le module, le lecteur fermerait le module sans
       eux, et le serveur figerait un score compte avec ces ecrans a 0. On relit
       donc le module, et on ne le ferme que s'il est, cote serveur, entierement
       fait (`fermetureApresRelecture`, parcours/progression.js). Sinon rien
       n'est ecrit, et la reprise posee par la relecture mene a ce qui reste.
       Relectures du meme jour sur 517eea8 puis 20deedc :
       - PENDANT la relecture, aucun clic ne fait avancer (un double clic
         fermait le module avant son retour, puis envoyait `resultFin: null`) ;
       - la relecture part APRES les enregistrements en vol : la reponse de
         l'ecran courant, envoyee dans le meme geste, doit etre en base, et sa
         fin d'envoi ne doit plus pouvoir reecrire la copie de reference ;
       - ⛔ si un enregistrement a echoue, meme en partie (relecture securite de
         7a2936e : `isDone` passe, `userChoice` echoue), on ne relit pas : la
         relecture remplacerait l'etat local, et la reponse non enregistree,
         qui n'est plus que la, serait perdue. On RENVOIE d'abord ce qui
         manque (la copie de reference n'a pas bouge, B41), et on ne relit
         que si tout est passe ;
       - delai maximal : une relecture qui ne revient pas ne fige pas les clics ;
       - relecture en echec ou trop lente : rien n'est ecrit, le clic suivant
         relit encore. */
    /* Jamais pendant un ESSAI depuis l'admin : le module en memoire est une
       copie, et la relecture le remplacerait par le parcours reel de l'admin. */
    if (index.chapitre === 'max' && partiesVidesSautees && !essaiRef.current) {
      const position = actIndex
      const avant = formation
      const numero = ++relectureNumero.current
      const encoreAttendue = () => relectureNumero.current === numero
      relectureEnCours.current = true
      const verification = enregistrementsEnVol.current
        .then(() => {
          if (!encoreAttendue()) return false
          // ce qui n'a pas pu partir ; rien si tout est passe (`enregistrer`
          // rend `false` sur echec)
          return saveUserFormation(avant).then((r) => r !== false, () => false)
        })
        .then((toutEstEnregistre) => (toutEstEnregistre && encoreAttendue() ? updateUserContext(undefined, false, encoreAttendue) : null))
      /* Le delai couvre TOUT, depuis le clic : l'attente des enregistrements
         comme la relecture (relecture de code de 7a2936e : un enregistrement
         qui ne revenait pas figeait « Suivant » sans fin). Depasse, la
         verification est abandonnee : elle ne posera plus rien. */
      let minuteur
      const delaiDepasse = new Promise((resoudre) => { minuteur = setTimeout(() => resoudre(null), DELAI_RELECTURE_MS) })
      return Promise.race([verification, delaiDepasse])
        .then((relue) => {
          const suite = fermetureApresRelecture(relue, avant, position)
          if (!suite) return
          setFormation(suite.formation)
          setActIndex(suite.index)
          return saveUserFormation(suite.formation)
        })
        .catch((e) => { console.log(e) })
        .finally(() => {
          clearTimeout(minuteur)
          if (encoreAttendue()) relectureNumero.current += 1   // ce qui tourne encore ne posera rien
          relectureEnCours.current = false
        })
    }
    setFormation(actFormation)
    saveUserFormation(actFormation)
    return setActIndex(index)
  }


  /* Tutoiement / vouvoiement (07/10/2026) : les alertes du navigateur
     ci-dessous suivent la cible du module en cours, par les deux memes
     fonctions que le reste de l'espace apprenant (utils/formeAdresse.js).
     Elles sont emises ICI, au-dessus du contexte de forme : on lit donc la
     cible directement sur `formation`, celle-la meme que ce contexte expose
     comme `actFormation`. Toujours en francais : ces alertes n'ont jamais eu
     de version anglaise, et l'anglais ne change pas. */
  const texteAlerte = (entree) => choisirTexte(entree, "fr", formeDepuisCible(formation?.cible))

  const sessionExpireeSignaleeRef = useRef(false) // B37
  /* B37 (25/09/2026) : session expiree (401). « Verifiez votre connexion puis
     reessayez » serait faux : reessayer echoue tant qu'on n'est pas
     reconnecte. On le dit, puis deconnexion propre et page de connexion ; a la
     reconnexion, le module reprend au premier ecran non enregistre (la base
     fait foi). Plusieurs enregistrements peuvent echouer ensemble (reponse
     puis ecran suivant) : un seul message, une seule deconnexion.
     27/09/2026 : sorti de saveUserFormation et expose par le contexte, pour
     que l'envoi de la reponse d'un candidat THE SCAN au serveur (FormatQuizz,
     chantier « scores cote serveur ») traite une session expiree exactement de
     la meme facon, sans recopier le message ni la deconnexion. */
  const signalerSessionExpiree = () => {
    if (sessionExpireeSignaleeRef.current) return
    sessionExpireeSignaleeRef.current = true
    window.alert(texteAlerte(DICTIONNAIRE.alertesApprenant.sessionExpiree))
    logout(() => { window.location.assign('/login') })
  }
  /* 27/09/2026, chantier « scores cote serveur » : la DEUXIEME sortie vers le
     serveur, pour la reponse d'un candidat THE SCAN que le serveur corrige
     (FormatQuizz). Elle passe par le contexte, et non directement par APIS,
     pour porter les memes gardes que l'enregistrement : rien ne part pendant
     un essai (`essaiRef`), rien ne part depuis l'apercu admin
     (`estApercuAdmin`). Refusee, elle rend un echec : l'ecran le dit, au lieu
     d'afficher une correction qu'aucun serveur n'a donnee. */
  const repondreSequence = async (elementID, userChoice) => {
    if (essaiRef.current || estApercuAdmin()) return { error: true, envoiBloque: true }
    return APIS.repondreSequence(elementID, userChoice)
  }

  /* 27/09/2026, chantier « scores cote serveur » (relecture, MOYEN 3). Pour un
     candidat THE SCAN, le serveur n'ecrit une reponse qu'une fois : si une
     reponse DIFFERENTE est deja en base (deux onglets, par exemple), il rend
     409 `dejaRepondu`. Traite comme une panne, cela bouclait : alerte, la
     reference n'etait pas mise a jour, chaque enregistrement suivant renvoyait
     le meme choix, recevait le meme refus, et le niveau module ne partait
     jamais. La base fait foi : on recharge l'etat depuis le serveur
     (updateUserContext), une seule fois a la fois, avec un seul message. La
     reference redevient celle de la base, et les enregistrements suivants
     partent normalement. */
  const resynchronisationRef = useRef(false)
  const resynchroniserAvecLaBase = () => {
    if (resynchronisationRef.current) return
    resynchronisationRef.current = true
    window.alert("Une autre réponse était déjà enregistrée pour cet écran, par exemple depuis un autre onglet. Le parcours reprend à partir de ce qui est enregistré.")
    Promise.resolve(updateUserContext())
      .catch(() => null)
      .then(() => { resynchronisationRef.current = false })
  }

  /* 27/09/2026 (relecture, point 6) : `options.reponseDejaEnregistree` dit que
     la reponse de cet ecran est DEJA en base (candidat THE SCAN : le serveur
     l'a enregistree en la corrigeant, /formation/sequence/repondre). Si ce
     renvoi echoue, « votre reponse n'a pas pu etre enregistree » serait faux :
     c'est la progression qui n'est pas passee, et elle repartira au prochain
     enregistrement (la reference n'est pas mise a jour). */
  /* B73 : chaque enregistrement est suivi, pour que la relecture d'avant une
     fin de module parte APRES lui (voir onIndexChange). Une reponse en vol
     serait sinon absente du module relu. */
  const saveUserFormation = (data, options) => {
    const envoi = enregistrer(data, options)
    enregistrementsEnVol.current = Promise.all([enregistrementsEnVol.current, envoi.catch(() => {})])
    return envoi
  }
  const enregistrer = async (data, options) => {
    /* ⛔ L'UNE DES DEUX SORTIES VERS LE SERVEUR, TOUTES DEUX COUPEES PENDANT UN
       ESSAI (l'autre est `repondreSequence`, juste au-dessus, 27/09/2026).
       Tout le reste du parcours vit en memoire. Ces sorties fermees, un essai
       ne peut RIEN enregistrer : ni progression, ni reponse, ni resultat.
       Accessoirement, cela evite aussi un plantage : la suite lit
       `sessionStorage.getItem('saveFormation')`, qui n'existe pas dans l'admin. */
    if (essaiRef.current) return
    data.progress = calculateProgress(data)
    let actFormation = JSON.parse(JSON.stringify(data))
    let compareForSave = JSON.parse(sessionStorage.getItem('saveFormation'))


    actFormation.chapitre.forEach((chap, indexChap) => {
      chap.partie.forEach((part, indexPart) => {
        part.sequence.forEach((seq, indexSeq) => {
          seq = sequencesDataRefacto(seq)
        })
      })
    })


    compareForSave.chapitre.forEach((chap, indexChap) => {
      chap.partie.forEach((part, indexPart) => {
        part.sequence.forEach((seq, indexSeq) => {
          seq = sequencesDataRefacto(seq)
        })
      })
    })

    var diffpatcher = jsondiffpatch.create({
      cloneDiffValues: true,
      propertyFilter: function(name, context) {
        if (name !== 'color') {
          return name;
        }
      },
      objectHash: function(obj, index) {
        return obj.id || '$$index:' + index;
      },
      arrays: {
          // default true, detect items moved inside the array (otherwise they will be registered as remove+add)
          detectMove: true,
          // default false, the value of items moved is not included in deltas
          includeValueOnMove: false,
      },
      textDiff: {
          minLength: 99999999999999,
      },
    });
    let delta = diffpatcher.diff(compareForSave, actFormation);
    if (delta) {
      let deltaFormat = jsondiffpatch.formatters.jsonpatch.format(delta)
      //console.log(deltaFormat)
      let changesArray = []
      //console.log(deltaFormat)
      deltaFormat.forEach((change) => {
        if (change.op === "replace") {
          let infosUpdate = deltaPathToElementReplace(actFormation, change.path)
          //console.log(infosUpdate)
          //console.log("change "+infosUpdate.type+" with resultID "+infosUpdate.el.resultID+", key "+infosUpdate.key+" to "+change.value)
          const envoi = async function() {
            return await APIS.editFormationResult(
              infosUpdate.el.id, //elementID (content)
              infosUpdate.type,
              {
                [infosUpdate.key] : change.value
              }
            )
          }
          envoi.niveau = infosUpdate.type
          changesArray.push(envoi)
        }
      });


      if (changesArray.length > 0) {
        //console.log(changesArray)
        /* B41 (25/09/2026) : tout partait en meme temps. Si la reponse echouait
           et que la progression du module passait, la base comptait une
           progression (21 -> 22 %) sans la reponse correspondante. Deux temps
           desormais : d'abord les reponses (sequences, parties, chapitres),
           puis le niveau module (progression, fin, resultats) SEULEMENT si
           toutes les reponses sont enregistrees. */
        const echoue = (r) => !r || r.error
        const reponses = changesArray.filter((f) => f.niveau !== 'content')
        const niveauModule = changesArray.filter((f) => f.niveau === 'content')
        let results = await Promise.all(reponses.map(f => f()));
        if (!results.some(echoue) && niveauModule.length > 0) {
          results = results.concat(await Promise.all(niveauModule.map(f => f())))
        }
        // Perte silencieuse : si une sauvegarde a échoué, on NE met PAS à jour la
        // référence locale (sinon le changement non enregistré sort du diff et
        // n'est jamais réessayé), on n'annonce pas de succès, et on prévient
        // l'apprenant. La réponse reste dans l'état local (déjà posée) : le
        // prochain enregistrement la renverra.
        if (results.some((r) => !r || r.error)) {
          // B37 (25/09/2026) : voir signalerSessionExpiree, plus haut.
          if (results.some((r) => r?.sessionExpiree)) {
            signalerSessionExpiree()
            return false
          }
          // 27/09/2026 : voir resynchroniserAvecLaBase, plus haut.
          if (results.some((r) => r?.dejaRepondu)) {
            resynchroniserAvecLaBase()
            return false
          }
          window.alert(options?.reponseDejaEnregistree === true
            ? texteAlerte(DICTIONNAIRE.alertesApprenant.progressionNonEnregistree)
            : texteAlerte(DICTIONNAIRE.formation.reponseNonEnregistree))
          return false
        }
        sessionStorage.setItem('saveFormation', JSON.stringify(data));
      }
    }

  }

  const deltaPathToElementReplace = (formation, path) => {
    let pathArr = path.substring(1).split('/')
    let keyToChange = pathArr[pathArr.length - 1]
    let element = {...formation};

    for (let i=0; i<pathArr.length-1; i++) {
      element = element[pathArr[i]]
    }

    return ({el : element, type : pathArr[pathArr.length-3] || 'content', key : keyToChange})
  }


  const getFormationResults = () => {
    let glbMasterPond = []
    /* 27/09/2026, chantier « scores cote serveur » : pour un candidat THE SCAN,
       le nouveau serveur ne livre plus glbMasterPond. `!== null` laissait alors
       passer `undefined`, et JSON.parse(undefined) levait une erreur. L'ecran
       de fin ne demande plus ce calcul dans ce cas (FinDeFormation), mais la
       fonction ne doit pas planter si un autre appelant le fait. */
    if (formation.glbMasterPond !== null && formation.glbMasterPond !== undefined) {
      glbMasterPond = JSON.parse(formation.glbMasterPond)
    } else if (formation.glbMasterPond === undefined) {
      /* 27/09/2026 (relecture, point 12) : une cle ABSENTE n'est attendue que
         chez un candidat note par le serveur, pour qui ce calcul ne tourne
         plus. Ailleurs, c'est une anomalie des donnees : on le trace plutot
         que de calculer en silence sans ponderation. */
      console.warn("getFormationResults : module " + formation.id + " sans glbMasterPond, calcul sans ponderation")
    }
    var categories = {master: { quizz : 0, questionnaireV2 : 0, points: 0, max: 0, score: 0 }}
    categories.glbMaster = { quizz : 0, questionnaireV2 : 0, points: 0, max: 0, score: 0 }
    const manageFormat = (format) => {
      if (['Quizz', 'QuestionnaireV2'].includes(format.type )) {
        const [points, max] = getPointsByQuizz(format)
        const makeResult = (c, points, max, format) => {
          let newPoints = c.points+points
          let newMax = c.max+max
          return {
            quizz : ['Quizz'].includes(format.type) ? c.quizz + 1 : c.quizz,
            questionnaireV2 : ['QuestionnaireV2'].includes(format.type) ? c.questionnaireV2 + 1 : c.questionnaireV2,
            points : newPoints,
            max : newMax,
            score : Math.round((newPoints / newMax) * 100)
          }
        }
        const makeResultGlbMaster = (c, points, max, format) => {
          //on cherche si la categorie compte : selection manuelle, jamais par defaut (07/10/2026)
          let statut = "non"
          let find = glbMasterPond.find((el) => el.key === format.categorie)
          if (find) {
            statut = find.value
          }
          if (statut === "oui") {
            let newPoints = c.points+points
            let newMax = c.max+max
            return {
              quizz : ['Quizz'].includes(format.type) ? c.quizz + 1 : c.quizz,
              questionnaireV2 : ['QuestionnaireV2'].includes(format.type) ? c.questionnaireV2 + 1 : c.questionnaireV2,
              points : newPoints,
              max : newMax,
              score : Math.round((newPoints / newMax) * 100)
            }
          } else {
            return {
              quizz : c.quizz,
              questionnaireV2 : c.questionnaireV2,
              points : c.points,
              max : c.max,
              score : Math.round((c.points / c.max) * 100),
            }
          }
        }
        categories.master = makeResult(categories.master, points, max, format)
        categories.glbMaster = makeResultGlbMaster(categories.glbMaster, points, max, format)
        if (format.categorie && format.categorie !== 'master' && format.categorie !== 'glbMaster') {
          categories[format.categorie] = makeResult(categories[format.categorie] ?? { points: 0, max: 0, score: 0, quizz : 0, questionnaireV2 : 0,  }, points, max, format)
        }
      }
    }

    let allDiagFormats = [];

    for (var iChap in formation.chapitre) {
      for (var iPart in formation.chapitre[iChap].partie) {
        for (var n in formation.chapitre[iChap].partie[iPart].sequence) {
          var format = formation.chapitre[iChap].partie[iPart].sequence[n]
          if (format.type === "Diagnostic") {
            allDiagFormats.push(format)
          }
          manageFormat(format)
        }
      }
    }

    /* REGLE DU 07/10/2026 (Nawal) : le score global n'est plus le total des
       points, mais la moyenne simple des categories (utils/scoreGlobal.js).
       master : toutes les categories du module ; glbMaster : celles marquees
       « oui » dans la ponderation, sans repli : rien de coche, pas de score de
       module (scoreDuModule, fiche scoring du 07/10). points et max restent les
       totaux, pour information. */
    categories.master.score = moyenneDesCategories(categories)
    categories.glbMaster.score = scoreDuModule(categories, glbMasterPond)

    //let lastDiagFormat = allDiagFormats[allDiagFormats.length - 1]

    //on regarde si on est en rattrapage ou non
    let doneInRat = false
    let formationID = formation.id
    let findInPlan = plan?.find((el) => el.id === formationID)
    //console.log(findInPlan)
    if (findInPlan) {
      if (findInPlan.isOpenForRat === true) {
        doneInRat = true
      }
    }

    return {categories : categories, doneInRat : doneInRat}
  }


  return (
    <UserContext.Provider
      value={{
        updateUserContext,
        currentUser : user,
        actPlan : plan,
        actFormation : formation,
        setActFormation : setFormation,
        actIndex,
        lastDoneIndex,
        allLoaded : loaded,
        manageFormatResponse,
        manageSlide,
        saveUserFormation,
        signalerSessionExpiree,
        repondreSequence,
        forceNewIndexes,
        getFormationResults,
        essai,
        demarrerEssai,
        arreterEssai,
        marquerApercuAdmin,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export default UserContext

export { UserProvider }
